// SETTINGS SEARCH — one pure function over the registry and the current values
// (design_handoff_settings principle 7: "Search is how you get around"). The
// Settings head box and ⌘K (ask-bar.js) use the SAME function, so a setting is
// found by what it is called, what it changes, where it sits, its key or its value.
// PURE: no browser imports (test/settings-search.test.mjs).
import { REGISTRY, fieldsOf, displayValue, rowPath, sectionById, groupLabel } from './settings-registry.js';

/** Module screens a setting also lives on, or whose figures a search for it wants ("Elsewhere"). */
export const ELSEWHERE = [
  { label: 'Accounts › VAT', sub: 'the period figures', view: 'accounts', words: 'vat return period quarter figures' },
  { label: 'Invoices › Overdue', sub: 'chasing, statements and late charges', view: 'invoices', words: 'chase chasing overdue reminder reminders late charge charges reference rate statement' },
  { label: 'Catalogue › Fee agent', sub: 'house pricing rules and levers', view: 'fee-agent', words: 'fee fees agent pricing rules levers proposal' },
  { label: 'Calendar › Time off rules', sub: 'how many off at once, half days, working from home', view: 'calendar', words: 'time off leave holiday holidays rules wfh half' },
  { label: 'Team › Signature', sub: 'each person’s signature', view: 'team', words: 'signature signatures email' },
  { label: 'Bank', sub: 'categories and the month lock', view: 'bank', words: 'bank lock locked month category categories' },
];

const norm = (s) => String(s == null ? '' : s).toLowerCase();

/** PURE — how well one term matches a set of strings: 100 exact, 60 prefix, 45 word prefix, 30 contains, 0 none. */
function termScore(t, vals) {
  let best = 0;
  for (const v of vals) {
    if (!v) continue;
    if (v === t) return 100;
    if (v.startsWith(t)) best = Math.max(best, 60);
    else if (v.split(/[\s.›·,/()-]+/).some((w) => w.startsWith(t))) best = Math.max(best, 45);
    else if (v.includes(t)) best = Math.max(best, 30);
  }
  return best;
}

/** PURE — every word a row is found by, weighted: its label first, then its effect, place, key and value. */
export function rowHaystack(entry, ctx = {}) {
  return {
    label: [norm(entry.label)],
    effect: [norm(entry.effect), ...(entry.usedBy || []).map(norm)],
    place: [norm(rowPath(entry)), norm((sectionById(entry.section) || {}).label), norm(groupLabel(entry.section, entry.group)), norm(entry.open && entry.open.label)],
    key: [norm(entry.id), ...fieldsOf(entry).map((f) => norm(f.key))],
    value: [norm(displayValue(entry, ctx))],
  };
}

/**
 * PURE — the rows a query finds, best first, and the module screens ("Elsewhere").
 * rows: the rows this person may see (registry.visibleRows) — never more.
 * ctx: { settings, org, docs } for the values. Every term must match something
 * (multi-word AND). Returns { hits: [{ entry, id, label, path, effect, value, score }], elsewhere: [{ label, sub, view }] }.
 */
export function searchSettings(query, rows = REGISTRY, ctx = {}, { limit = 8 } = {}) {
  const terms = norm(query).trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return { hits: [], elsewhere: [] };
  const W = { label: 1, effect: 0.6, place: 0.5, key: 0.7, value: 0.55 };
  const found = [];
  for (const e of rows) {
    const h = rowHaystack(e, ctx);
    let total = 0; let ok = true;
    for (const t of terms) {
      let best = 0;
      for (const [k, vals] of Object.entries(h)) best = Math.max(best, termScore(t, vals) * W[k]);
      if (!best) { ok = false; break; }
      total += best;
    }
    if (!ok) continue;
    found.push({ entry: e, id: e.id, label: e.label, path: rowPath(e), effect: e.effect || '', value: displayValue(e, ctx), score: total / terms.length });
  }
  found.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  const elsewhere = ELSEWHERE.filter((x) => terms.every((t) => termScore(t, [norm(x.label), norm(x.words), norm(x.sub)]) > 0))
    .map(({ label, sub, view }) => ({ label, sub, view }));
  return { hits: found.slice(0, limit), elsewhere: elsewhere.slice(0, 3) };
}
